import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { UserCheck, RefreshCw, ExternalLink } from "lucide-react";

export default function AssignCharts() {
  const [charts, setCharts] = useState([]);
  const [qas, setQas] = useState([]);
  const [selectedQa, setSelectedQa] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/charts/unassigned"),
      api.get("/users/qa-list"),
    ]).then(([c, q]) => {
      setCharts(c.data);
      setQas(q.data);
      if (q.data.length && !selectedQa) setSelectedQa(q.data[0].id);
      setSelected(new Set());
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === charts.length) setSelected(new Set());
    else setSelected(new Set(charts.map((c) => c.id)));
  };

  const assign = async () => {
    if (!selectedQa) return toast.error("Pick a QA first.");
    if (selected.size === 0) return toast.error("Select at least one chart.");
    setSubmitting(true);
    try {
      const r = await api.post("/charts/bulk-assign", {
        chart_ids: Array.from(selected),
        qa_id: selectedQa,
      });
      toast.success(`Assigned ${r.data.assigned} chart(s) to ${r.data.qa_name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="assign-charts-page">
      <PageHeader
        title="Assign Charts"
        subtitle="Select one or many pending charts and route them to a QA in a single action."
        actions={
          <button onClick={load} className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900" data-testid="refresh-btn">
            <RefreshCw size={14}/> Refresh
          </button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Assign selected to</label>
          <select value={selectedQa} onChange={(e) => setSelectedQa(e.target.value)} data-testid="qa-select"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
            {qas.length === 0 && <option value="">No QA users available</option>}
            {qas.map((q) => <option key={q.id} value={q.id}>{q.name} — {q.email}</option>)}
          </select>
        </div>
        <button onClick={assign} disabled={submitting || selected.size === 0 || !selectedQa}
                data-testid="bulk-assign-btn"
                className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
          <UserCheck size={16}/> {submitting ? "Assigning…" : `Assign ${selected.size || ""} chart${selected.size === 1 ? "" : "s"}`.trim()}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading unassigned charts…</div>
        ) : charts.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No unassigned charts — everything is routed.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input type="checkbox"
                         checked={selected.size === charts.length && charts.length > 0}
                         onChange={toggleAll}
                         data-testid="select-all-checkbox"
                         className="rounded border-slate-300"/>
                </th>
                <th className="px-3 py-2 text-left">Chart</th>
                <th className="px-3 py-2 text-left">Patient</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">M1021</th>
                <th className="px-3 py-2 text-left">Coder</th>
                <th className="px-3 py-2 text-left">Submitted</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {charts.map((c) => (
                <tr key={c.id} className={`hover:bg-slate-50 ${selected.has(c.id) ? "bg-sky-50/40" : ""}`} data-testid={`chart-row-${c.chart_id}`}>
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)}
                           data-testid={`chart-checkbox-${c.chart_id}`}
                           className="rounded border-slate-300"/>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{c.chart_id}</td>
                  <td className="px-3 py-2.5">{c.patient_name}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{c.assessment_type}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">
                    {c.m1021_primary ? <span className="bg-sky-50 text-sky-700 rounded px-1.5 py-0.5">{c.m1021_primary}</span> : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{c.submitted_by_name}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{c.submitted_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Link to={`/charts/${c.id}`} className="text-slate-400 hover:text-sky-700 inline-flex" data-testid={`chart-view-${c.chart_id}`}>
                      <ExternalLink size={14}/>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
